import { useEffect, useMemo } from 'react';
import {
  ButtonSecondary,
  ButtonSize,
  Filters,
  IconArrowLeft,
  IconArrowRight,
  IconDown,
  IconUp,
  SchemaFilterType,
  Table,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../card/card';
import {
  marginListColumns,
  marginListColumnsLabels,
  marginListFiltersSchema,
  marginListSchema,
  allMarginListColumnsLabels,
  allMarginListFiltersSchema,
  allMarginListSchema,
} from '../../../hoc/customers/variables/margins';
import { CustomMargin } from '../../../hoc/customers/types';
import {
  useLazyGetCustomMarginsQuery,
  useLazyGetCustomerDefaultMarginQuery,
} from '../../../hoc/customers/store/services/customers';
import { CustomMarginTypes } from './types';

import cn from './tariffs.module.css';

let focusTimeout: NodeJS.Timeout;

const formatMarginsData = (list: CustomMargin[]): CustomMargin[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let feesSettings = <div className={cn.defaultFee}>DEFAULT</div>;

    if (result.isCustom && result.tariffId && !result.organizationId) {
      feesSettings = <div className={cn.customFee}>CUSTOM</div>;
    } else if (result.isCustom && result.organizationId && !result.tariffId) {
      feesSettings = <div className={cn.userFee}>USER</div>;
    }

    const render: Record<string, React.JSX.Element> = {
      rateSourceId: <div>{result.rateSource.name}</div>,
      currencyFromId: <div>{result.currencyFrom.name}</div>,
      currencyToId: <div>{result.currencyTo.name}</div>,
      isCustom: <div>{feesSettings}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CustomMargins = ({
  allMarginsVisible,
  setAllMarginsVisible,
  setSelectedCurrency,
  currencies,
  currenciesList,
  selectedCurrency,
  showMoreCurrencies,
  setShowMoreCurrencies,
  setMenuOnFocus,
  customMargins,
  customerId,
  tariffId,
  customMarginsPagination,
  menuOnFocus,
  rateSources,
  setSeletedMarginId,
  setMarginPanelIsOpen,
  defaultMargin,
}: CustomMarginTypes) => {
  const [
    getCustomMargins,
    { isLoading: marginsIsLoading, isFetching: marginsIsFetching },
  ] = useLazyGetCustomMarginsQuery();
  const [getDefaultMargin] = useLazyGetCustomerDefaultMarginQuery();
  const { filters, onFilterChange } = useQueryState(marginListSchema);
  const {
    filters: allMarginFilters,
    onFilterChange: allMarginsOnFilterChange,
  } = useQueryState(allMarginListSchema);

  const { settings } = useAppSelector((state) => state.root);

  const { onFilter, errorList } = useGetList<any>({
    getList: getCustomMargins,
    onFilterChange,
    onSortChange: () => {},
    schema: marginListSchema,
  });

  const {
    onFilter: allMarginsOnFilter,
    errorList: allMarginsErrorsList,
    setPage,
  } = useGetList<any>({
    getList: getCustomMargins,
    onFilterChange: allMarginsOnFilterChange,
    onSortChange: () => {},
    schema: allMarginListSchema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: marginListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const {
    filtersComponent: allMarginsFitlersComponent,
    setValues: allMarginsSetValues,
    handleSubmit: allMarginsHandleSubmit,
  } = useFilters({
    filtersSchema: allMarginListFiltersSchema,
    filters: allMarginFilters,
    onFilterChange: allMarginsOnFilterChange,
  });

  useEffect(() => {
    if (showMoreCurrencies && !menuOnFocus) {
      focusTimeout = setTimeout(() => {
        setShowMoreCurrencies(false);
      }, 2000);
    }
  }, [showMoreCurrencies, menuOnFocus]);

  useEffect(() => {
    if (rateSources && currencies) {
      setValues(
        'rateSourceId',
        rateSources?.map((item) => ({ name: item.name, value: item.id })),
      );

      allMarginsSetValues(
        'rateSourceId',
        rateSources?.map((item) => ({ name: item.name, value: item.id })),
      );
      allMarginsSetValues('currencyToId', currencies);
    }
  }, [rateSources, currencies]);

  useEffect(() => {
    if (!defaultMargin) {
      getDefaultMargin();
    }
  }, [defaultMargin]);

  useEffect(() => {
    if (tariffId && !allMarginsVisible) {
      const currentFilters: Filters = {
        organizationId: {
          value: [customerId as string],
          type: SchemaFilterType.STRING,
        },
        createType: {
          value: ['custom'],
          type: SchemaFilterType.STRING,
        },
        take: {
          value: ['6'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(currentFilters);
    }
  }, [tariffId, allMarginsVisible]);

  useEffect(() => {
    if (allMarginsVisible && currencies && tariffId && settings) {
      const baseRateSource = settings?.find(
        (item) => item.key === 'EXCHANGE-RATE-SOURCE',
      );

      const currentFilters: Filters = {
        currencyFromId: {
          value: [currencies[0].value as string],
          type: SchemaFilterType.STRING,
        },
        organizationId: {
          value: [customerId as string],
          type: SchemaFilterType.STRING,
        },
        tariffId: {
          value: [tariffId],
          type: SchemaFilterType.STRING,
        },
        take: {
          value: ['20'],
          type: SchemaFilterType.STRING,
        },
        rateSourceId: {
          value: [baseRateSource?.data?.value as string],
          type: SchemaFilterType.STRING,
        },
      };

      allMarginsOnFilter(currentFilters);
    }
  }, [currencies, allMarginsVisible, settings]);

  const formattedMarginsData = useMemo(() => {
    const data = formatMarginsData(customMargins as CustomMargin[]);

    return data;
  }, [customMargins, filters]);

  return (
    <Card
      className={cn.marginCard}
      transparent={!marginsIsLoading}
      isLoading={marginsIsLoading}
    >
      {allMarginsVisible && (
        <button
          type="button"
          className={cn.marginsBackButton}
          onClick={() => {
            setAllMarginsVisible(false);
            setSelectedCurrency('');
          }}
        >
          <IconArrowLeft width={13} height={11} />
          Back to Tariff
        </button>
      )}
      <div className={cn.marginsTitleWrapper}>
        <div className={cn.marginsTitle}>FX margin</div>
        {!allMarginsVisible && (
          <ButtonSecondary
            size={ButtonSize.SMALL}
            text="See all"
            icon={<IconArrowRight />}
            onClick={() => {
              setAllMarginsVisible(true);

              if (currencies) {
                setSelectedCurrency(currencies[0].value as string);
              }
            }}
            className={`button-white ${cn.allMarginsButton}`}
            iconPosition="right"
          />
        )}
      </div>
      {allMarginsVisible && (
        <div className={cn.currenciesTabsWrapper}>
          <div className={cn.currenciesListWrapper}>
            {currenciesList?.slice(0, 7)?.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${cn.currencyTab} ${
                  selectedCurrency === item.id ? cn.active : ''
                }`}
                onClick={() => {
                  setSelectedCurrency(item.id);

                  allMarginsOnFilter({
                    ...allMarginFilters,
                    currencyFromId: {
                      value: [item.id],
                      type: SchemaFilterType.STRING,
                    },
                    organizationId: {
                      value: [customerId as string],
                      type: SchemaFilterType.STRING,
                    },
                    tariffId: {
                      value: [tariffId as string],
                      type: SchemaFilterType.STRING,
                    },
                  });
                }}
              >
                {item.name}
              </button>
            ))}
            {currenciesList && currenciesList?.length > 7 && (
              <div className={cn.moreCurrenciesWrapper}>
                <button
                  type="button"
                  className={`${cn.moreCurrenciesButton} ${cn.currencyTab} ${
                    showMoreCurrencies ? cn.active : ''
                  }`}
                  onMouseEnter={() => {
                    setShowMoreCurrencies(true);
                    setMenuOnFocus(true);
                  }}
                  onMouseLeave={() => setMenuOnFocus(false)}
                >
                  More {!showMoreCurrencies ? <IconDown /> : <IconUp />}
                </button>
                {showMoreCurrencies && (
                  <ul
                    onMouseEnter={() => {
                      setMenuOnFocus(true);
                      clearTimeout(focusTimeout);
                    }}
                    className={cn.moreCurernciesList}
                    onMouseLeave={() => {
                      setShowMoreCurrencies(false);
                      clearTimeout(focusTimeout);
                    }}
                  >
                    {currenciesList?.slice(7).map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCurrency(item.id);

                            allMarginsOnFilter({
                              ...allMarginFilters,
                              currencyFromId: {
                                value: [item.id],
                                type: SchemaFilterType.STRING,
                              },
                              organizationId: {
                                value: [customerId as string],
                                type: SchemaFilterType.STRING,
                              },
                              tariffId: {
                                value: [tariffId as string],
                                type: SchemaFilterType.STRING,
                              },
                            });
                          }}
                          className={
                            selectedCurrency === item.id ? cn.active : ''
                          }
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={`${!customMargins ? cn.marginsTable : ''} ${
          cn.marginTableWrapper
        }`}
      >
        {!allMarginsVisible && (
          <Table
            columns={marginListColumns}
            data={formattedMarginsData || []}
            sort={{}}
            onSort={() => {}}
            error={errorList}
            filters={filters}
            columnsLabels={marginListColumnsLabels}
            tableFormClassName={
              !customMargins?.length ? cn.customMarginsEmptyTableForm : ''
            }
            tableClassName={cn.marginsTable}
            tableTopClassName={cn.marginTableTop}
            onRowClick={(id) => {
              setSeletedMarginId(id);
              setMarginPanelIsOpen(true);
            }}
            onFilterChange={(values) => {
              const currentFilters: Filters = {
                ...values,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
                createType: {
                  value: ['custom'],
                  type: SchemaFilterType.STRING,
                },
                take: {
                  value: ['6'],
                  type: SchemaFilterType.STRING,
                },
              };

              onFilter(currentFilters);
            }}
            tableBodyClassName={`${
              !customMargins?.length ? cn.marginsEmptyTable : ''
            } ${cn.marginTableBody}`}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={marginsIsFetching || marginsIsLoading}
            take="20"
          />
        )}
        {allMarginsVisible && (
          <Table
            columns={marginListColumns}
            data={formattedMarginsData || []}
            sort={{}}
            onSort={() => {}}
            tableTopClassName={cn.marginTableTop}
            error={allMarginsErrorsList}
            filters={allMarginFilters}
            columnsLabels={allMarginListColumnsLabels}
            tableClassName={cn.marginsTable}
            onRowClick={(id) => {
              setSeletedMarginId(id);
              setMarginPanelIsOpen(true);
            }}
            onFilterChange={(values) => {
              const currentFilters: Filters = {
                ...values,
                currencyFromId: {
                  value: [selectedCurrency as string],
                  type: SchemaFilterType.STRING,
                },
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
                tariffId: {
                  value: [tariffId as string],
                  type: SchemaFilterType.STRING,
                },
                take: {
                  value: ['20'],
                  type: SchemaFilterType.STRING,
                },
              };

              allMarginsOnFilter(currentFilters);
            }}
            tableBodyClassName={`${
              !customMargins?.length ? cn.marginsEmptyTable : ''
            } ${cn.marginTableBody}`}
            filtersComponent={allMarginsFitlersComponent}
            handleSubmit={allMarginsHandleSubmit}
            isLoading={marginsIsFetching || marginsIsLoading}
            setPage={(data) =>
              setPage({
                ...data,
                filters: {
                  ...allMarginFilters,
                  currencyFromId: {
                    value: [selectedCurrency as string],
                    type: SchemaFilterType.STRING,
                  },
                  organizationId: {
                    value: [customerId as string],
                    type: SchemaFilterType.STRING,
                  },
                  tariffId: {
                    value: [tariffId as string],
                    type: SchemaFilterType.STRING,
                  },
                },
              })
            }
            take="20"
            pagination={customMarginsPagination}
          />
        )}
      </div>
    </Card>
  );
};

export default CustomMargins;
